import React from 'react';
import { useTranslation } from 'react-i18next';

import Grid from '@mui/material/Grid';
import Menu from '@mui/material/Menu';
import Table from '@mui/material/Table';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InputAdornment from '@mui/material/InputAdornment';
import TableContainer from '@mui/material/TableContainer';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';

import Iconify from '../../theme/iconify/iconify';

import { fDate } from '../util/format-time';

function UserTable({ user, users, sortedUsers, setSortedUsers, userId, setUserId, menuAnchor, setMenuAnchor, setDeleteUserModal, changeRole, menu }) {
    const [filteredUsers, setFilteredUsers] = React.useState(sortedUsers);
    const [search, setSearch] = React.useState('');
    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState(null);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(parseInt(localStorage.getItem('mISLec_table_rows_per_page')) || 5);
    const [startIndex, setStartIndex] = React.useState(null);
    const [endIndex, setEndIndex] = React.useState(null);

    const { i18n, t } = useTranslation();

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value));
        localStorage.setItem('mISLec_table_rows_per_page', parseInt(event.target.value));
        if (parseInt(event.target.value) === -1) {
            setStartIndex(0);
            setEndIndex(sortedUsers.length);
        } else {
            setStartIndex(page * parseInt(event.target.value));
            setEndIndex(page * parseInt(event.target.value) + parseInt(event.target.value));
        }
        setPage(0);
    };

    const handleRequestSort = (event, property) => {
        if (orderBy === property) {
            if (order === 'asc') {
                setOrder('desc');
            } else {
                setSortedUsers(users);
                setOrderBy(null);
                setOrder('asc');
            }
        } else {
            setOrderBy(property);
            setOrder('asc');
        }
    }

    React.useEffect(() => {
        if (!users) return;

        let sorted = [...users];
        if (orderBy) {
            sorted.sort((a, b) => {
                if (orderBy === 'last_login' || orderBy === 'registration_date') {
                    const valueA = a[orderBy] ? new Date(a[orderBy]) : order === 'asc' ? Infinity : -Infinity;
                    const valueB = b[orderBy] ? new Date(b[orderBy]) : order === 'asc' ? Infinity : -Infinity;
                    return (valueA - valueB) * (order === 'desc' ? -1 : 1);
                } else {
                    return (a[orderBy] < b[orderBy] ? -1 : 1) * (order === 'desc' ? -1 : 1);
                }
            });
        }

        setSortedUsers(sorted);
    }, [users, orderBy, order]);

    React.useEffect(() => {
        setFilteredUsers(
            sortedUsers
                .filter(u =>
                    u.email.toLowerCase().includes(search.toLowerCase()) ||
                    u.fname.toLowerCase().includes(search.toLowerCase()) ||
                    u.lname.toLowerCase().includes(search.toLowerCase()) ||
                    `${u.fname.toLowerCase()} ${u.lname.toLowerCase()}`.includes(search.toLowerCase()) ||
                    t(`users.role.${u.role}`).toLowerCase().includes(search.toLowerCase())
                )
        );
    }, [search, sortedUsers]);

    React.useEffect(() => {
        if (!filteredUsers) return;

        if (rowsPerPage === -1) {
            setStartIndex(0);
            setEndIndex(filteredUsers.length);
        } else {
            setStartIndex(page * rowsPerPage);
            setEndIndex(page * rowsPerPage + rowsPerPage);
        }
    }, [filteredUsers, page, rowsPerPage]);

    React.useEffect(() => {
        setPage(0);
    }, [search]);

    return (
        <Grid container spacing={1}>
            <Grid item xs={12} md={4}>
                <TextField
                    id='mISLec_user_management_search'
                    placeholder={t('users.inputs.search.label')}
                    variant='outlined'
                    size='small'
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position='end'>
                                <IconButton disabled edge='start'>
                                    <Iconify icon='ion:search' />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                    sx={{
                        '& .MuiInputBase-input': { fontSize: '0.875rem' },
                    }}
                />
            </Grid>
            <Grid item xs={12}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ backgroundColor: '#FFFFFF' }}>
                                    <TableSortLabel
                                        active={orderBy === 'fname'}
                                        direction={orderBy === 'fname' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'fname')}
                                    >
                                        <Typography sx={{ fontSize: '0.875rem', fontWeight: '700 !important' }}>
                                            {t('users.table.name')}
                                        </Typography>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ backgroundColor: '#FFFFFF', maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    <TableSortLabel
                                        active={orderBy === 'email'}
                                        direction={orderBy === 'email' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'email')}
                                    >
                                        <Typography sx={{ fontSize: '0.875rem', fontWeight: '700 !important' }}>
                                            {t('users.table.email')}
                                        </Typography>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ backgroundColor: '#FFFFFF' }}>
                                    <TableSortLabel
                                        active={orderBy === 'registration_date'}
                                        direction={orderBy === 'registration_date' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'registration_date')}
                                    >
                                        <Typography sx={{ fontSize: '0.875rem', fontWeight: '700 !important' }}>
                                            {t('users.table.added')}
                                        </Typography>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ backgroundColor: '#FFFFFF' }}>
                                    <TableSortLabel
                                        active={orderBy === 'last_login'}
                                        direction={orderBy === 'last_login' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'last_login')}
                                    >
                                        <Typography sx={{ fontSize: '0.875rem', fontWeight: '700 !important' }}>
                                            {t('users.table.active')}
                                        </Typography>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ backgroundColor: '#FFFFFF' }}>
                                    <TableSortLabel
                                        active={orderBy === 'role'}
                                        direction={orderBy === 'role' ? order : 'asc'}
                                        onClick={(event) => handleRequestSort(event, 'role')}
                                    >
                                        <Typography sx={{ fontSize: '0.875rem', fontWeight: '700 !important' }}>
                                            {t('users.table.role')}
                                        </Typography>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ width: '5%', backgroundColor: '#FFFFFF' }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredUsers
                                .slice(startIndex, endIndex)
                                .map((u, index) => (
                                    <TableRow key={index} sx={{
                                        '&:nth-of-type(odd)': {
                                            backgroundColor: 'background.default',
                                        }
                                    }}>
                                        <TableCell>
                                            <Typography sx={{ fontSize: '0.875rem' }}>
                                                {`${u.fname} ${u.lname}`}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography sx={{ fontSize: '0.875rem' }}>
                                                <a href={`mailto:${u.email}`}>
                                                    <span style={{ display: 'inline-block', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'middle' }}>
                                                        {u.email}
                                                    </span>
                                                </a>
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography sx={{ fontSize: '0.875rem' }}>
                                                {u.registration_date ? fDate(u.registration_date, i18n.language) : t('users.table.noRegistration')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography sx={{ fontSize: '0.875rem' }}>
                                                {u.last_login ? fDate(u.last_login, i18n.language) : t('users.table.noActivity')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                label={t('users.table.role')}
                                                className='mISLec_user_management_role'
                                                size='small'
                                                value={u.role}
                                                onChange={(e) => changeRole(e, u)}
                                                disabled={u._id === user._id}
                                                select
                                                fullWidth
                                                sx={{
                                                    '& .MuiInputLabel-root': { fontSize: '0.875rem' }
                                                }}
                                            >
                                                <MenuItem value='student'>
                                                    <ListItemText>
                                                        {t('users.role.student')}
                                                    </ListItemText>
                                                </MenuItem>
                                                <MenuItem value='professor'>
                                                    <ListItemText>
                                                        {t('users.role.professor')}
                                                    </ListItemText>
                                                </MenuItem>
                                                <MenuItem value='admin'>
                                                    <ListItemText>
                                                        {t('users.role.admin')}
                                                    </ListItemText>
                                                </MenuItem>
                                            </TextField>
                                        </TableCell>
                                        <TableCell align='right'>
                                            <IconButton
                                                disableRipple
                                                disableFocusRipple
                                                disabled={u._id === user._id}
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    setUserId(u._id);
                                                    setMenuAnchor(event.currentTarget);
                                                }}
                                                sx={{ marginRight: 'auto', zIndex: 9, pr: 0, py: 0 }}
                                            >
                                                <Tooltip title={t('users.table.settings')} arrow>
                                                    <MoreVertIcon fontSize='small' />
                                                </Tooltip>
                                            </IconButton>
                                            <Menu
                                                key={u._id}
                                                anchorEl={menuAnchor}
                                                open={menu && userId === u._id}
                                                onClose={(event) => {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    setMenuAnchor(null);
                                                    setUserId(null);
                                                }}
                                            >
                                                <MenuItem
                                                    sx={{ color: 'error.main' }}
                                                    onClick={(event) => {
                                                        event.preventDefault();
                                                        setUserId(u._id);
                                                        setDeleteUserModal(true);
                                                        setMenuAnchor(null);
                                                    }}
                                                >
                                                    <ListItemIcon sx={{ color: 'error.main', minWidth: '28px !important' }}>
                                                        <Iconify icon='eva:trash-outline' />
                                                    </ListItemIcon>
                                                    <ListItemText>
                                                        {t('users.table.terminate')}
                                                    </ListItemText>
                                                </MenuItem>
                                            </Menu>
                                        </TableCell>
                                    </TableRow>
                                ))
                            }
                            {filteredUsers.length === 0 &&
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Grid container>
                                            <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
                                                <Iconify icon='pajamas:search-results' width={36} sx={{ mr: 2 }} />
                                                <Grid item xs={12} md={4}>
                                                    <Typography variant='h6' sx={{ fontSize: '0.875rem !important' }}>
                                                        {t('users.table.empty.title')}
                                                    </Typography>
                                                    <Typography variant='caption' sx={{ fontSize: '0.8rem !important' }}>
                                                        {t('users.table.empty.description')}
                                                    </Typography>
                                                    <Typography variant='caption' sx={{ fontSize: '0.8rem !important', display: 'block' }}>
                                                        {t('users.table.empty.tip')}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </TableCell>
                                </TableRow>
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
                {filteredUsers.length > 0 && (
                    <TablePagination
                        component='div'
                        rowsPerPageOptions={[5, 10, 25, 50, { value: -1, label: t('users.table.all') }]}
                        count={filteredUsers.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage={t('users.table.rows')}
                        labelDisplayedRows={({ from, to, count }) => { return `${from}–${to} ${t('util.pagination.of')} ${count !== -1 ? count : `${t('users.table.moreThan')} ${to}`}`; }}
                    />
                )}
            </Grid>
        </Grid>
    );
}

export default UserTable;